import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  IconButton,
  TablePagination,
  InputBase,
  Button,
  Avatar,
  CircularProgress,
  Alert,
  Menu,
  MenuItem,
  Stack,
  FormControl,
  InputLabel,
  Select,
  Divider,
  Snackbar,
  alpha,
  useTheme,
  useMediaQuery,
  Tooltip
} from '@mui/material';
import PageHeader from '../components/common/PageHeader';
import {
  AdminDialog,
  AdminDialogHeader,
  AdminDialogContent,
  AdminDialogActions,
  DeleteConfirmDialog,
  FilterDrawer
} from '../components/common/modal';
import EmptyState from '../components/common/EmptyState';
import ExportTableButton from '../components/common/ExportTableButton';

import {
  MoreVertical,
  Search,
  Filter,
  CheckCircle2,
  Trash2,
  Clock,
  ExternalLink,
  Phone,
  User,
  MapPin,
  MessageSquare,
  AlertCircle,
  RefreshCw,
  Layers,
  Users,
} from 'lucide-react';
import axios from '../utils/axios';
import { useDispatch, useSelector } from 'react-redux';
import { fetchEnquiries, updateEnquiryStatus, deleteEnquiry, setEnquirySubCategory, bulkRequeueUnmatched } from '../redux/slices/enquirySlice';
import { fetchCategories, fetchStates, fetchCities, fetchSubCategories } from '../redux/slices/businessSlice';

const GeneralEnquiriesView = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { enquiries, totalEnquiries, isLoading, error } = useSelector((state) => state.enquiry);
  const { categories, states, cities, subCategories } = useSelector((state) => state.business);

  // States for search and pagination
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // States for filters
  const [categoryFilter, setCategoryFilter] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  // Row actions
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
  const [openBulkRequeueDialog, setOpenBulkRequeueDialog] = useState(false);
  const [isRequeuing, setIsRequeuing] = useState(false);

  // Dispatched sellers breakdown for the selected enquiry
  const [dispatchedSellers, setDispatchedSellers] = useState([]);
  const [loadingSellers, setLoadingSellers] = useState(false);

  // Fetch dispatched sellers whenever details dialog opens
  useEffect(() => {
    if (openDetailsDialog && selectedEnquiry?._id) {
      setLoadingSellers(true);
      axios.get(`/generalResellerEnquiry/${selectedEnquiry._id}/sellers`, { skipNotify: true })
        .then((res) => {
          if (res.data?.success) {
            setDispatchedSellers(res.data.sellers || []);
          } else {
            setDispatchedSellers([]);
          }
        })
        .catch(() => setDispatchedSellers([]))
        .finally(() => setLoadingSellers(false));
    } else {
      setDispatchedSellers([]);
    }
  }, [openDetailsDialog, selectedEnquiry?._id]);

  // Snackbar state
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Triage: Services enquiries the lead cron could not route because nobody
  // recorded WHICH service the buyer wanted. They are held, not dispatched, so
  // no seller pays for a lead they cannot serve — but they need a human to fill
  // in the service before they can go out.
  const [unroutableOnly, setUnroutableOnly] = useState(false);
  const [subCatDialog, setSubCatDialog] = useState({ open: false, enquiry: null });
  const [chosenSubCat, setChosenSubCat] = useState('');
  const [savingSubCat, setSavingSubCat] = useState(false);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(0);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const loadEnquiries = useCallback(() => {
    dispatch(fetchEnquiries({
      page: page + 1,
      limit: rowsPerPage,
      search: debouncedSearch,
      category_name: categoryFilter,
      state: stateFilter,
      city: cityFilter,
      is_processed: statusFilter === 'unmatched' ? undefined : (statusFilter === '' ? undefined : statusFilter),
      close_reason: statusFilter === 'unmatched' ? 'unmatched' : (unroutableOnly ? 'service_sub_category_missing' : undefined)
    }));
  }, [dispatch, page, rowsPerPage, debouncedSearch, categoryFilter, stateFilter, cityFilter, statusFilter, unroutableOnly]);

  useEffect(() => {
    loadEnquiries();
  }, [loadEnquiries]);

  useEffect(() => {
    // Fetch filter options if not already available
    if (categories.length === 0) dispatch(fetchCategories());
    if (states.length === 0) dispatch(fetchStates());
    if (cities.length === 0) dispatch(fetchCities());
    if (!subCategories || subCategories.length === 0) dispatch(fetchSubCategories());
  }, [dispatch, categories.length, states.length, cities.length, subCategories]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenActionMenu = (event, enquiry) => {
    setActionMenuAnchor(event.currentTarget);
    setSelectedEnquiry(enquiry);
  };

  const handleCloseActionMenu = () => {
    setActionMenuAnchor(null);
  };

  // Sub-categories belonging to the enquiry's own category, so the dialog can
  // only offer services that actually exist under it.
  const subCatOptionsFor = (enquiry) => (subCategories || [])
    .filter((sc) => sc?.categories?.[0]?.category_name === enquiry?.category_name)
    .map((sc) => sc.sub_category_name)
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b));

  const handleSaveSubCategory = async () => {
    if (!subCatDialog.enquiry || !chosenSubCat) return;
    setSavingSubCat(true);
    try {
      const resultAction = await dispatch(setEnquirySubCategory({
        id: subCatDialog.enquiry._id,
        sub_category_name: chosenSubCat
      }));
      if (setEnquirySubCategory.fulfilled.match(resultAction)) {
        setSnackbar({
          open: true,
          message: resultAction.payload?.msg
            || `Service set to "${chosenSubCat}". It will be dispatched on the next lead run.`,
          severity: 'success'
        });
        setSubCatDialog({ open: false, enquiry: null });
        setChosenSubCat('');
        loadEnquiries();
      } else {
        setSnackbar({ open: true, message: resultAction.payload || 'Could not set the service', severity: 'error' });
      }
    } catch (err) {
      setSnackbar({ open: true, message: 'An error occurred', severity: 'error' });
    } finally {
      setSavingSubCat(false);
    }
  };

  const handleToggleStatus = async () => {
    if (!selectedEnquiry) return;

    try {
      const resultAction = await dispatch(updateEnquiryStatus({
        id: selectedEnquiry._id,
        is_processed: !selectedEnquiry.is_processed
      }));

      if (updateEnquiryStatus.fulfilled.match(resultAction)) {
        setSnackbar({
          open: true,
          message: `Enquiry marked as ${!selectedEnquiry.is_processed ? 'processed' : 'pending'} successfully!`,
          severity: 'success'
        });
      } else {
        setSnackbar({ open: true, message: resultAction.payload || 'Update failed', severity: 'error' });
      }
    } catch (err) {
      setSnackbar({ open: true, message: 'An error occurred', severity: 'error' });
    }
    handleCloseActionMenu();
  };

  const handleDeleteEnquiry = async () => {
    if (!selectedEnquiry) return;
    try {
      const resultAction = await dispatch(deleteEnquiry(selectedEnquiry._id));
      if (deleteEnquiry.fulfilled.match(resultAction)) {
        setSnackbar({ open: true, message: 'Enquiry deleted successfully', severity: 'success' });
        setOpenDeleteDialog(false);
      } else {
        setSnackbar({ open: true, message: resultAction.payload || 'Delete failed', severity: 'error' });
      }
    } catch (err) {
      setSnackbar({ open: true, message: 'An error occurred', severity: 'error' });
    }
  };

  const handleBulkRequeueConfirm = async () => {
    setIsRequeuing(true);
    try {
      const res = await dispatch(bulkRequeueUnmatched()).unwrap();
      setSnackbar({
        open: true,
        message: res?.msg || 'All unmatched enquiries successfully requeued to Pending!',
        severity: 'success'
      });
      setOpenBulkRequeueDialog(false);
      loadEnquiries();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err || 'Failed to requeue unmatched enquiries',
        severity: 'error'
      });
    } finally {
      setIsRequeuing(false);
    }
  };

  const handleResetFilters = () => {
    setCategoryFilter('');
    setStateFilter('');
    setCityFilter('');
    setStatusFilter('');
    setSearchTerm('');
    setPage(0);
    setIsFilterOpen(false);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const norm = (val) => String(val || '').trim().toLowerCase();
  const selectedFilterStateObj = Array.isArray(states)
    ? states.find(s => norm(s.state_name) === norm(stateFilter) || String(s._id) === String(stateFilter))
    : null;

  const filteredDrawerCities = stateFilter && Array.isArray(cities)
    ? Array.from(
      new Map(
        cities
          .filter(c => {
            if (norm(c.state?.state_name) === norm(stateFilter)) return true;
            if (typeof c.state === 'string' && norm(c.state) === norm(stateFilter)) return true;
            if (norm(c.state_name) === norm(stateFilter)) return true;
            if (selectedFilterStateObj && (
              String(c.state_id) === String(selectedFilterStateObj._id) ||
              String(c.state_id?._id) === String(selectedFilterStateObj._id) ||
              String(c.state?._id) === String(selectedFilterStateObj._id)
            )) return true;
            return false;
          })
          .map(c => [norm(c.city_name), c])
      ).values()
    ).sort((a, b) => (a.city_name || '').localeCompare(b.city_name || ''))
    : (Array.isArray(cities) ? cities : []);

  return (
    <Box sx={{ py: 1 }}>
      <PageHeader
        title="General Enquiries"
        subtitle="Manage inbound customer requirements, assign services, and monitor automated seller matches."
        count={totalEnquiries}
        countLabel="enquiries"
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Engagement', path: '/enquiries' },
          { label: 'General Enquiries' },
        ]}
        action={
          <Button
            variant="contained"
            color="warning"
            startIcon={<RefreshCw size={18} />}
            onClick={() => setOpenBulkRequeueDialog(true)}
            disabled={isRequeuing}
          >
            {isRequeuing ? 'Requeuing...' : 'Requeue All Unmatched to Pending'}
          </Button>
        }
      />

      <Card sx={{ borderRadius: 4, overflow: 'hidden', position: 'relative' }}>
        {isLoading && (
          <Box sx={{
            position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.6), zIndex: 10, backdropFilter: 'blur(2px)'
          }}>
            <CircularProgress thickness={4} size={40} sx={{ color: 'primary.main' }} />
          </Box>
        )}

        <Box sx={{ p: { xs: 2, sm: 3 }, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' }, justifyContent: 'space-between', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', px: 1, py: 0.5, border: 1, borderColor: 'divider', borderRadius: 2, width: { xs: '100%', sm: 320 } }}>
            <Search size={18} color="#637381" />
            <InputBase
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value.trimStart())}
              placeholder="Search by name, phone or message..."
              sx={{ ml: 1, flex: 1, fontSize: '0.875rem' }}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', justifyContent: { xs: 'flex-end', sm: 'flex-start' } }}>
            {(categoryFilter || stateFilter || cityFilter || statusFilter || searchTerm) && (
              <Button size="small" onClick={handleResetFilters} sx={{ textTransform: 'none', fontWeight: 600, color: '#FF4842' }}>
                Reset
              </Button>
            )}
            <IconButton aria-label="Filters" onClick={() => setIsFilterOpen(true)} sx={{ color: 'text.secondary' }}>
              <Filter size={20} />
            </IconButton>
            <ExportTableButton
              data={enquiries}
              total={totalEnquiries}
              onExportAll={async () => {
                const res = await axios.get('/generalResellerEnquiry', {
                  params: {
                    all: true,
                    search: debouncedSearch || undefined,
                    category_name: categoryFilter || undefined,
                    state: stateFilter || undefined,
                    city: cityFilter || undefined,
                    is_processed: statusFilter === 'unmatched' ? undefined : (statusFilter === '' ? undefined : statusFilter),
                    close_reason: statusFilter === 'unmatched' ? 'unmatched' : (unroutableOnly ? 'service_sub_category_missing' : undefined),
                  },
                });
                return res.data.enquiries || [];
              }}
              columns={[
                { label: 'Name', value: (r) => `${r.first_name || ''} ${r.last_name || ''}`.trim() || 'N/A' },
                { label: 'Phone', key: 'phone' },
                { label: 'Type', value: (r) => r.enquiry_type || 'reseller' },
                { label: 'Category', key: 'category_name' },
                { label: 'Sub-Category', value: (r) => r.sub_category_name || 'N/A' },
                { label: 'City', key: 'city' },
                { label: 'State', key: 'state' },
                { label: 'Quantity', value: (r) => r.quantity || 'N/A' },
                { label: 'Message', key: 'Inquiry_Massage' },
                { label: 'Leads Dispatched', key: 'leads_dispatched' },
                { label: 'Status', value: (r) => r.close_reason ? `Closed (${r.close_reason})` : (r.is_processed ? 'Processed' : 'Pending') },
                { label: 'Date', value: (r) => r.created_at ? new Date(r.created_at).toLocaleString() : 'N/A' },
              ]}
              filename="general_enquiries"
            />
          </Box>
        </Box>

        {error ? (
          <Box sx={{ px: 3, pb: 3 }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        ) : (
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table sx={{ minWidth: 800 }}>
              <TableHead sx={{ backgroundColor: 'background.neutral' }}>
                <TableRow>
                  <TableCell sx={{ color: 'text.secondary', fontWeight: 700 }}>Reseller</TableCell>
                  <TableCell sx={{ color: 'text.secondary', fontWeight: 700 }}>Enquiry Details</TableCell>
                  <TableCell sx={{ color: 'text.secondary', fontWeight: 700 }}>Location</TableCell>
                  <TableCell sx={{ color: 'text.secondary', fontWeight: 700 }}>Date</TableCell>
                  <TableCell sx={{ color: 'text.secondary', fontWeight: 700 }}>Sellers Sent</TableCell>
                  <TableCell sx={{ color: 'text.secondary', fontWeight: 700 }}>Status</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {enquiries && enquiries.length > 0 ? enquiries.map((enquiry) => (
                  <TableRow key={enquiry._id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{
                          width: 40, height: 40, borderRadius: '50%',
                          bgcolor: (theme) => alpha(theme.palette.info.main, 0.1), color: 'info.main',
                          display: 'flex', alignItems: 'center', justifyContent: 'center'
                        }}>
                          <User size={20} />
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {enquiry.first_name} {enquiry.last_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Phone size={12} /> {enquiry.phone}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: 'info.main' }}>
                        {enquiry.category_name}
                        {enquiry.sub_category_name ? ` › ${enquiry.sub_category_name}` : ''}
                      </Typography>
                      {enquiry.close_reason === 'service_sub_category_missing' && (
                        <Tooltip title="This enquiry was NOT sent to any provider: we don't know which service the buyer wants, and the category alone would send it to unrelated providers. Set the service to release it.">
                          <Chip
                            size="small"
                            label="Service missing — held"
                            sx={{
                              my: 0.4, height: 20, fontSize: 10.5, fontWeight: 800,
                              backgroundColor: (t) => alpha(t.palette.warning.main, 0.14),
                              color: 'warning.dark'
                            }}
                          />
                        </Tooltip>
                      )}
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: 'block',
                          maxWidth: 250,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}
                      >
                        {enquiry.Inquiry_Massage}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{enquiry.city}</Typography>
                      <Typography variant="caption" color="text.secondary">{enquiry.state}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{formatDate(enquiry.created_at).split(',')[0]}</Typography>
                      <Typography variant="caption" color="text.secondary">{formatDate(enquiry.created_at).split(',')[1]}</Typography>
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const dispatched = enquiry.leads_dispatched || 0;
                        if (!enquiry.is_processed) {
                          return (
                            <Tooltip title="This enquiry is actively being routed by the cron queue">
                              <Chip
                                icon={<Clock size={13} />}
                                label="0 (In Queue)"
                                size="small"
                                variant="outlined"
                                sx={{
                                  fontWeight: 600,
                                  borderColor: (theme) => alpha(theme.palette.warning.main, 0.45),
                                  color: 'warning.dark',
                                  '& .MuiChip-icon': { color: 'inherit' }
                                }}
                              />
                            </Tooltip>
                          );
                        }
                        if (enquiry.close_reason === 'unmatched') {
                          return (
                            <Tooltip title="No matching active sellers found in this city">
                              <Chip
                                label="0 (Unmatched)"
                                size="small"
                                sx={{
                                  fontWeight: 600,
                                  bgcolor: (t) => alpha(t.palette.grey[500], 0.12),
                                  color: 'text.secondary'
                                }}
                              />
                            </Tooltip>
                          );
                        }
                        return (
                          <Tooltip title={`Dispatched to ${dispatched} verified seller${dispatched === 1 ? '' : 's'}`}>
                            <Chip
                              icon={<Users size={14} />}
                              label={`${dispatched} ${dispatched === 1 ? 'Seller' : 'Sellers'}`}
                              size="small"
                              sx={{
                                fontWeight: 700,
                                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
                                color: 'primary.dark',
                                '& .MuiChip-icon': { color: 'inherit' }
                              }}
                            />
                          </Tooltip>
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      {(() => {
                        if (!enquiry.is_processed) {
                          const attempts = enquiry.process_attempts || 0;
                          return (
                            <Tooltip title={`Hourly cron checked ${attempts} time${attempts === 1 ? '' : 's'}. Actively searching for matching sellers in ${enquiry.city} with lead balance.`}>
                              <Chip
                                label={attempts > 0 ? `Pending (${attempts})` : 'Pending'}
                                size="small"
                                icon={<Clock size={14} />}
                                sx={{
                                  fontWeight: 700,
                                  backgroundColor: (theme) => alpha(theme.palette.warning.main, 0.12),
                                  color: 'warning.dark',
                                  '& .MuiChip-icon': { color: 'inherit' }
                                }}
                              />
                            </Tooltip>
                          );
                        }
                        if (enquiry.close_reason === 'unmatched') {
                          return (
                            <Tooltip title={`No active registered seller in ${enquiry.city} within ${enquiry.process_attempts || 72} hours. Auto-closed as unmatched.`}>
                              <Chip
                                label="Unmatched"
                                size="small"
                                icon={<AlertCircle size={14} />}
                                sx={{
                                  fontWeight: 700,
                                  backgroundColor: (theme) => alpha(theme.palette.grey[500], 0.14),
                                  color: 'text.secondary',
                                  '& .MuiChip-icon': { color: 'inherit' }
                                }}
                              />
                            </Tooltip>
                          );
                        }
                        const dispatched = enquiry.leads_dispatched || 0;
                        return (
                          <Tooltip title={dispatched > 0 ? `Dispatched to ${dispatched} verified seller${dispatched === 1 ? '' : 's'}` : 'Processed'}>
                            <Chip
                              label={dispatched > 0 ? `Dispatched (${dispatched})` : 'Processed'}
                              size="small"
                              icon={<CheckCircle2 size={14} />}
                              sx={{
                                fontWeight: 700,
                                backgroundColor: (theme) => alpha(theme.palette.success.main, 0.12),
                                color: 'success.dark',
                                '& .MuiChip-icon': { color: 'inherit' }
                              }}
                            />
                          </Tooltip>
                        );
                      })()}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton aria-label="Row actions" size="small" onClick={(e) => handleOpenActionMenu(e, enquiry)}>
                        <MoreVertical size={20} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                )) : !isLoading && (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ p: 0 }}>
                      <EmptyState
                        icon={MessageSquare}
                        title="No enquiries found"
                        description="Customer inquiries and leads will appear here."
                      />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: { xs: 1, sm: 2 } }}>
          <TablePagination
            component="div"
            count={totalEnquiries || 0}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50]}
            labelRowsPerPage={isMobile ? "" : "Rows per page:"}
            sx={{
              border: 'none',
              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-input': {
                display: isMobile ? 'none' : 'block',
              },
            }}
          />
        </Box>
      </Card>

      {/* Details Dialog */}
      <AdminDialog
        open={openDetailsDialog}
        onClose={() => setOpenDetailsDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <AdminDialogHeader
          title="Enquiry Details"
          subtitle="Review buyer interest and lead routing status"
          icon={MessageSquare}
          onClose={() => setOpenDetailsDialog(false)}
        />
        <AdminDialogContent dividers>
          <Stack spacing={3}>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Buyer / Reseller Info
              </Typography>
              <Box sx={{ display: 'flex', mt: 1, gap: 2, alignItems: 'center' }}>
                <Avatar sx={{ bgcolor: (theme) => alpha(theme.palette.info.main, 0.12), color: 'info.main', fontWeight: 700 }}>
                  {selectedEnquiry?.first_name?.[0]}
                </Avatar>
                <Box>
                  <Typography variant="subtitle1" fontWeight={700}>
                    {selectedEnquiry?.first_name} {selectedEnquiry?.last_name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Phone size={14} /> {selectedEnquiry?.phone}
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Interest & Message
              </Typography>
              <Box sx={{ mt: 1, p: 2, bgcolor: 'background.neutral', borderRadius: 2 }}>
                <Typography variant="subtitle2" color="primary.main" gutterBottom fontWeight={700}>
                  Category: {selectedEnquiry?.category_name}
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {selectedEnquiry?.Inquiry_Massage}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Location
                </Typography>
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                  <MapPin size={14} /> {selectedEnquiry?.city}, {selectedEnquiry?.state}
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Source
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {selectedEnquiry?.source || 'Direct'}
                </Typography>
              </Box>
            </Box>

            {/* Cron & Lead Dispatch Details */}
            <Box sx={{ p: 2, bgcolor: 'background.neutral', borderRadius: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block' }}>
                  Cron Dispatch Status
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>
                  {selectedEnquiry?.is_processed
                    ? (selectedEnquiry?.close_reason === 'unmatched'
                      ? 'Closed (No matching seller in city)'
                      : `Delivered to ${selectedEnquiry?.leads_dispatched || dispatchedSellers.length || 0} seller(s)`)
                    : `Active in Queue (${selectedEnquiry?.process_attempts || 0} checks)`}
                </Typography>
              </Box>
              {selectedEnquiry?.last_processed_at && (
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="caption" color="text.secondary">Last Checked</Typography>
                  <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>
                    {formatDate(selectedEnquiry.last_processed_at)}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Dispatched Sellers Breakdown Section */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Users size={16} /> Dispatched Sellers Breakdown ({selectedEnquiry?.leads_dispatched || dispatchedSellers.length || 0})
                </Typography>
                {loadingSellers && <CircularProgress size={16} />}
              </Box>

              {loadingSellers ? (
                <Box sx={{ py: 3, textAlign: 'center' }}>
                  <CircularProgress size={24} />
                </Box>
              ) : dispatchedSellers.length > 0 ? (
                <Stack spacing={1.5}>
                  {dispatchedSellers.map((item, idx) => (
                    <Box
                      key={item.urgent_enquiry_id || idx}
                      sx={{
                        p: 1.75,
                        borderRadius: 2,
                        border: 1,
                        borderColor: 'divider',
                        bgcolor: (theme) => alpha(theme.palette.background.paper, 0.5),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2,
                        flexWrap: { xs: 'wrap', sm: 'nowrap' }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar sx={{ width: 38, height: 38, bgcolor: (t) => alpha(t.palette.primary.main, 0.12), color: 'primary.main', fontSize: '0.9rem', fontWeight: 700 }}>
                          {item.business?.business_name?.[0] || item.seller?.first_name?.[0] || 'S'}
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                            {item.business?.business_name || 'Business (Direct)'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                            <User size={12} /> {item.seller ? `${item.seller.first_name || ''} ${item.seller.last_name || ''}`.trim() : 'Seller'}
                            {item.seller?.phone && ` • ${item.seller.phone}`}
                          </Typography>
                          {(item.business?.city || item.business?.state) && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                              📍 {item.business.city}{item.business.state ? `, ${item.business.state}` : ''}
                            </Typography>
                          )}
                        </Box>
                      </Box>

                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip
                          size="small"
                          label={item.delivery_status === 'locked' ? 'Locked (Paywall)' : 'Delivered'}
                          color={item.delivery_status === 'locked' ? 'warning' : 'success'}
                          variant={item.delivery_status === 'locked' ? 'outlined' : 'filled'}
                          sx={{ fontWeight: 700, fontSize: '0.72rem', height: 22 }}
                        />
                        {item.status && (
                          <Chip
                            size="small"
                            label={`Status: ${item.status}`}
                            sx={{ fontWeight: 600, fontSize: '0.72rem', height: 22 }}
                          />
                        )}
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              ) : (
                <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'background.neutral', textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    {!selectedEnquiry?.is_processed
                      ? 'Waiting in queue. Cron will match this lead with registered sellers in the target city.'
                      : selectedEnquiry?.close_reason === 'unmatched'
                      ? 'No matching sellers were found in this city/category.'
                      : 'No seller delivery records found for this enquiry.'}
                  </Typography>
                </Box>
              )}
            </Box>
          </Stack>
        </AdminDialogContent>
        <AdminDialogActions>
          <Button onClick={() => setOpenDetailsDialog(false)} color="inherit" sx={{ fontWeight: 600 }}>
            Close
          </Button>
          {selectedEnquiry?.is_processed ? (
            <Button
              startIcon={<RefreshCw size={18} />}
              variant="contained"
              color="warning"
              onClick={async () => {
                await dispatch(updateEnquiryStatus({ id: selectedEnquiry._id, is_processed: false }));
                setOpenDetailsDialog(false);
                setSnackbar({ open: true, message: 'Enquiry returned to active cron queue for matching!', severity: 'success' });
              }}
              sx={{ px: 3, fontWeight: 700 }}
            >
              Retry Matching in Cron
            </Button>
          ) : (
            <Button
              startIcon={<CheckCircle2 size={18} />}
              variant="contained"
              color="primary"
              onClick={async () => {
                await dispatch(updateEnquiryStatus({ id: selectedEnquiry._id, is_processed: true }));
                setOpenDetailsDialog(false);
                setSnackbar({ open: true, message: 'Enquiry marked as processed', severity: 'success' });
              }}
              sx={{ px: 3, fontWeight: 700 }}
            >
              Mark as Processed
            </Button>
          )}
        </AdminDialogActions>
      </AdminDialog>

      {/* Filter Sidebar Drawer */}
      <FilterDrawer
        open={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        title="Filter Enquiries"
        activeCount={[statusFilter, categoryFilter, stateFilter, cityFilter, unroutableOnly ? 'held' : ''].filter(Boolean).length}
        width={400}
        onReset={handleResetFilters}
        onApply={() => setIsFilterOpen(false)}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
              sx={{ borderRadius: 1.5 }}
            >
              <MenuItem value="">All Statuses</MenuItem>
              <MenuItem value="false">Pending (In Queue)</MenuItem>
              <MenuItem value="unmatched">Unmatched (No Sellers in City)</MenuItem>
              <MenuItem value="true">Processed / Dispatched</MenuItem>
            </Select>
          </FormControl>

          {/* Quick toggle for held enquiries */}
          <Button
            fullWidth
            variant={unroutableOnly ? 'contained' : 'outlined'}
            color="warning"
            onClick={() => { setUnroutableOnly((v) => !v); setPage(0); }}
            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 1.5 }}
          >
            {unroutableOnly ? 'Showing held enquiries' : 'Held: service missing'}
          </Button>

          <FormControl fullWidth size="small">
            <InputLabel>Category</InputLabel>
            <Select
              value={categoryFilter}
              label="Category"
              onChange={(e) => setCategoryFilter(e.target.value)}
              sx={{ borderRadius: 1.5 }}
            >
              <MenuItem value="">All Categories</MenuItem>
              {categories.map((cat) => (
                <MenuItem key={cat._id} value={cat.category_name}>{cat.category_name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel>State</InputLabel>
            <Select
              value={stateFilter}
              label="State"
              onChange={(e) => { setStateFilter(e.target.value); setCityFilter(''); setPage(0); }}
              sx={{ borderRadius: 1.5 }}
            >
              <MenuItem value="">All States</MenuItem>
              {states.map((s) => (
                <MenuItem key={s._id} value={s.state_name}>{s.state_name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small" disabled={!stateFilter}>
            <InputLabel>City</InputLabel>
            <Select
              value={cityFilter}
              label="City"
              onChange={(e) => { setCityFilter(e.target.value); setPage(0); }}
              sx={{ borderRadius: 1.5 }}
            >
              <MenuItem value="">All Cities</MenuItem>
              {filteredDrawerCities.map((c) => (
                <MenuItem key={c._id} value={c.city_name}>{c.city_name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </FilterDrawer>

      {/* Action Menu */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={handleCloseActionMenu}
        slotProps={{
          paper: { sx: { width: 190, p: 0.5 } }
        }}
      >
        <MenuItem onClick={() => { setOpenDetailsDialog(true); handleCloseActionMenu(); }} sx={{ gap: 1.25, borderRadius: 1 }}>
          <ExternalLink size={16} /> View Details
        </MenuItem>
        <MenuItem
          onClick={() => {
            setChosenSubCat(selectedEnquiry?.sub_category_name || '');
            setSubCatDialog({ open: true, enquiry: selectedEnquiry });
            handleCloseActionMenu();
          }}
          sx={{ gap: 1.25, borderRadius: 1 }}
        >
          <Filter size={16} /> Set Service
        </MenuItem>
        <MenuItem onClick={handleToggleStatus} sx={{ gap: 1.25, borderRadius: 1 }}>
          {selectedEnquiry?.is_processed ? (
            <><RefreshCw size={16} style={{ color: theme.palette.warning.main }} /> Retry in Cron</>
          ) : (
            <><CheckCircle2 size={16} style={{ color: theme.palette.success.main }} /> Mark Processed</>
          )}
        </MenuItem>
        <Divider sx={{ my: 0.5 }} />
        <MenuItem onClick={() => { setOpenDeleteDialog(true); handleCloseActionMenu(); }} sx={{ color: 'error.main', gap: 1.25, borderRadius: 1 }}>
          <Trash2 size={16} /> Delete
        </MenuItem>
      </Menu>

      {/* Set-service dialog — releases a held enquiry back into the lead queue */}
      <AdminDialog
        open={subCatDialog.open}
        onClose={() => setSubCatDialog({ open: false, enquiry: null })}
        maxWidth="sm"
      >
        <AdminDialogHeader
          title="Which service does buyer want?"
          subtitle="Select specific sub-category to route lead accurately"
          icon={Layers}
          onClose={() => setSubCatDialog({ open: false, enquiry: null })}
        />
        <AdminDialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {subCatDialog.enquiry?.category_name} is a broad category, so the lead cron will not
            send this enquiry until it knows the exact service — otherwise it would go to
            providers who cannot serve it. Saving here puts it back in the queue for the next run.
          </Typography>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>Service</InputLabel>
            <Select
              value={chosenSubCat}
              label="Service"
              onChange={(e) => setChosenSubCat(e.target.value)}
            >
              {subCatOptionsFor(subCatDialog.enquiry).map((name) => (
                <MenuItem key={name} value={name}>{name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {subCatOptionsFor(subCatDialog.enquiry).length === 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              No sub-categories exist under “{subCatDialog.enquiry?.category_name}”. Add one under
              Sub Categories first.
            </Alert>
          )}
        </AdminDialogContent>
        <AdminDialogActions>
          <Button onClick={() => setSubCatDialog({ open: false, enquiry: null })} color="inherit" sx={{ fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={!chosenSubCat || savingSubCat}
            onClick={handleSaveSubCategory}
            sx={{ px: 3, fontWeight: 700 }}
          >
            {savingSubCat ? 'Saving…' : 'Save & Requeue'}
          </Button>
        </AdminDialogActions>
      </AdminDialog>

      {/* Delete Dialog */}
      <DeleteConfirmDialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        onConfirm={handleDeleteEnquiry}
        itemName={selectedEnquiry ? `${selectedEnquiry.first_name} ${selectedEnquiry.last_name || ''}`.trim() : 'this enquiry'}
        itemType="Enquiry"
      />

      {/* Bulk Requeue Unmatched Confirmation Dialog */}
      <AdminDialog
        open={openBulkRequeueDialog}
        onClose={() => !isRequeuing && setOpenBulkRequeueDialog(false)}
        maxWidth="xs"
      >
        <AdminDialogHeader
          title="Requeue All Unmatched"
          subtitle="Reset unmatched leads back to pending"
          icon={RefreshCw}
          iconColor="warning"
          onClose={() => !isRequeuing && setOpenBulkRequeueDialog(false)}
        />
        <AdminDialogContent dividers>
          <Typography variant="body2" color="text.primary" sx={{ mb: 2 }}>
            This will update <strong>all unmatched enquiries</strong> back to <strong>Pending</strong>:
          </Typography>
          <Box sx={{ p: 2, bgcolor: 'background.neutral', borderRadius: 2 }}>
            <Typography variant="body2" sx={{ mb: 0.8 }}>
              • Resets <strong>process attempts to 0</strong>
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.8 }}>
              • Clears the <strong>unmatched</strong> close reason
            </Typography>
            <Typography variant="body2">
              • The hourly cron will immediately start checking them again for matching sellers.
            </Typography>
          </Box>
        </AdminDialogContent>
        <AdminDialogActions>
          <Button onClick={() => setOpenBulkRequeueDialog(false)} disabled={isRequeuing} color="inherit" sx={{ fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            onClick={handleBulkRequeueConfirm}
            disabled={isRequeuing}
            variant="contained"
            color="warning"
            sx={{
              fontWeight: 700,
              px: 3
            }}
          >
            {isRequeuing ? 'Requeuing...' : 'Confirm Requeue'}
          </Button>
        </AdminDialogActions>
      </AdminDialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} variant="filled" sx={{ borderRadius: 2 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GeneralEnquiriesView;
